import { Automation, AutomationSettingValues } from '@maximizer/actions/domain';
import { AiUserDataGroup } from '@maximizer/core/shared/data-access';
import { PreferenceFieldViewModel, PreferenceViewModel, ToolDefinition, ToolViewModel } from '../models/ai-hub.models';
import { buildAccessControl } from './access-control';
import { PREFERENCE_BASE, preferenceSummary } from './preference-summary';
import { effectiveSettings, isKnownSetting } from './preference-values';

export const TOOL_IMAGE_PATH = 'assets/images/previews/ai-hub';

export interface ToolViewModelInput {
  definitions: readonly ToolDefinition[];
  group: AiUserDataGroup;
  iqBoostOn: boolean;
  isSLE: boolean;
  cdn: string;
  isFeatureOn: (key: string, defaultValue: boolean) => boolean;
}

function versioned(base: string, isSLE: boolean): string {
  return `${base}.${isSLE ? 'defaultVersion' : 'faVersion'}`;
}

export function buildToolViewModels(input: ToolViewModelInput): ToolViewModel[] {
  const { group, iqBoostOn, isSLE, cdn, isFeatureOn } = input;

  return input.definitions
    .filter((definition) => isFeatureOn(definition.visibilityFlag, false))
    .map((definition) => {
      const base = `aiHub.tools.${definition.key}`;
      const access = buildAccessControl({
        iqBoostOn,
        isAdmin: group.isAdmin,
        orgAvailable: group.admin.aiFeaturesEnabled[definition.key],
        userEnabled: group.user.aiFeaturesEnabled[definition.key],
        orgGateOpen: !definition.orgGateFlag || isFeatureOn(definition.orgGateFlag, false),
      });
      const onForYou = access.mine.checked;

      return {
        key: definition.key,
        icon: definition.icon,
        nameKey: `${base}.name`,
        moduleKey: versioned(`${base}.module`, isSLE),
        descriptionKey: versioned(`${base}.description`, isSLE),
        noteKey: versioned(`${base}.note`, isSLE),
        imageUrl: definition.imageFile ? `${cdn}/${TOOL_IMAGE_PATH}/${definition.imageFile}` : null,
        previewDescriptionKey: `${base}.previewDescription`,
        anonymizerNoteKey: definition.hasAnonymizerNote ? `${base}.anonymizerNote` : null,
        sourceKeys: definition.sources[isSLE ? 'default' : 'fa'].map((source) => `aiHub.tools.sources.${source}`),
        links: definition.links,
        app: definition.app && isFeatureOn(definition.app.featureFlagId, false) ? definition.app : null,
        access,
        dimmed: !(onForYou || (group.isAdmin && access.org?.checked === true)),
        launchDisabled: !onForYou,
      };
    });
}

export interface PreferenceViewModelInput {
  automations: readonly Automation[];
  isAdmin: boolean;
  iqBoostOn: boolean;
  openSettings: Readonly<Record<string, boolean>>;
  formatTime: (date: Date) => string;
}

function tagKeys(automation: Automation): string[] {
  const tags: string[] = [];
  if (automation.triggerTypes.includes('event')) tags.push('aiHub.preferences.tags.eventTriggered');
  else if (automation.triggerTypes.includes('schedule')) tags.push('aiHub.preferences.tags.scheduled');
  if (automation.reviewRequired) tags.push('aiHub.preferences.tags.reviewRequired');
  return tags;
}

function preferenceFields(automation: Automation, values: AutomationSettingValues): PreferenceFieldViewModel[] {
  const base = `${PREFERENCE_BASE}.${automation.id}.settings`;

  return automation.settings.filter(isKnownSetting).map((setting) => ({
    name: setting.name,
    kind: setting.type,
    label: { key: `${base}.${setting.name}.label`, fallback: setting.label },
    options:
      setting.type === 'select'
        ? setting.options.map((option) => ({
            value: option.value,
            label: { key: `${base}.${setting.name}.options.${option.value}`, fallback: option.text },
          }))
        : [],
    value: values[setting.name],
  }));
}

export function buildPreferenceViewModels(input: PreferenceViewModelInput): PreferenceViewModel[] {
  const { isAdmin, iqBoostOn, openSettings, formatTime } = input;

  return input.automations.map((automation) => {
    const { id } = automation;
    const values = effectiveSettings(automation.settings, automation.values);
    const access = buildAccessControl({
      iqBoostOn,
      isAdmin,
      orgAvailable: automation.available,
      userEnabled: automation.enabled,
      orgGateOpen: true,
      onSummary: preferenceSummary({ automationId: id, settings: automation.settings, values, formatTime }),
    });
    const fields = preferenceFields(automation, values);
    const showSettingsButton = fields.length > 0 && access.mine.checked;

    return {
      id,
      icon: automation.icon,
      name: automation.name,
      description: automation.description,
      tagKeys: tagKeys(automation),
      access,
      showSettingsButton,
      settingsOpen: showSettingsButton && openSettings[id] !== false,
      fields,
    };
  });
}
